import { useEffect, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, ConfirmDialog, EmptyState, Input, LoadingState } from "@agentnative/ui";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { getOrganization, getOrganizationTree, updateOrganization } from "../lib/api/org";
import { apiErrorMessage } from "../lib/api/client";
import { getOrgPolicy, setOrgPolicy } from "../lib/api/oauth";

export function OrganizationSettingsRoute() {
  const organizationId = useCurrentOrganizationId();

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: () => getOrganization(organizationId),
    enabled: !!organizationId,
  });
  // The tree is members-only and optional: a failure just hides the card.
  const treeQuery = useQuery({
    queryKey: ["organization-tree", organizationId],
    queryFn: () => getOrganizationTree(organizationId),
    enabled: !!organizationId,
    retry: false,
  });

  // Only owners, admins and platform admins can read the policy; otherwise the card is hidden.
  const policyQuery = useQuery({
    queryKey: ["org-policy", organizationId],
    queryFn: () => getOrgPolicy(organizationId),
    enabled: !!organizationId,
    retry: false,
  });
  const [confirmOff, setConfirmOff] = useState(false);
  const [affected, setAffected] = useState<number | null>(null);
  const policyMutation = useMutation({
    mutationFn: (allow: boolean) => setOrgPolicy(organizationId, allow),
    onSuccess: (r, allow) => {
      setConfirmOff(false);
      setAffected(allow ? null : r.grants_affected);
      queryClient.invalidateQueries({ queryKey: ["org-policy", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
    },
  });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const loaded = query.data?.organization;
  useEffect(() => {
    if (loaded) {
      setName(loaded.name);
      setSlug(loaded.slug);
    }
  }, [loaded?.id, loaded?.name, loaded?.slug]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateOrganization(organizationId, {
        name: name !== loaded?.name ? name : undefined,
        slug: slug !== loaded?.slug ? slug : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["organization-tree", organizationId] });
    },
  });

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Set an organization_id in the dev auth panel above to view its settings."
      />
    );
  }

  if (query.isLoading) {
    return <LoadingState message="Loading organization..." />;
  }

  if (query.isError) {
    return <Alert variant="danger" title="Failed to load organization">{apiErrorMessage(query.error)}</Alert>;
  }

  const org = query.data!.organization;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Organization</h1>
      <Card padding="lg">
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Name</dt>
              <dd className="text-base">{org.name}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Slug</dt>
              <dd className="font-mono text-base">{org.slug}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Organization ID</dt>
              <dd className="font-mono text-sm">{org.id}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Owner ID</dt>
              <dd className="font-mono text-sm">{org.ownerId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Status</dt>
              <dd>
                <Badge variant={org.enabled ? "success" : "danger"}>
                  {org.enabled ? "Enabled" : "Disabled"}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Created</dt>
              <dd className="text-sm">{new Date(org.createdAt).toLocaleString()}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>
      <Card padding="lg">
        <CardBody>
          <h2 className="mb-3 text-lg font-semibold">Edit</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
            <Input
              label="Slug"
              value={slug}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSlug(e.target.value)}
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              disabled={
                saveMutation.isPending ||
                (name === org.name && slug === org.slug) ||
                name.trim() === "" ||
                slug === ""
              }
              onClick={() => saveMutation.mutate()}
            >
              Save
            </Button>
            <span className="text-xs text-[var(--color-neutral-500)]">
              Only an owner can change these. Slugs are 3-63 characters of a-z, 0-9 and hyphen.
            </span>
          </div>
          {saveMutation.isError && (
            <div className="mt-3">
              <Alert variant="danger" title="Could not save">
                {apiErrorMessage(saveMutation.error)}
              </Alert>
            </div>
          )}
        </CardBody>
      </Card>

      {policyQuery.data && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-1 text-lg font-semibold">Connectors and agents</h2>
            <p className="mb-3 text-sm text-[var(--color-neutral-500)]">
              When on, members can connect this organization to apps such as Claude connectors, and agents can be bound to it. When off,
              the organization is excluded from every connector and agent token and cannot be selected when someone connects an app.
            </p>
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                role="switch"
                checked={policyQuery.data.allow_connectors}
                disabled={policyMutation.isPending}
                onChange={(e) => (e.target.checked ? policyMutation.mutate(true) : setConfirmOff(true))}
              />
              <span>{policyQuery.data.allow_connectors ? "Allowed" : "Turned off"}</span>
              <Badge variant={policyQuery.data.allow_connectors ? "success" : "warning"}>
                {policyQuery.data.allow_connectors ? "On" : "Off"}
              </Badge>
            </label>
            {affected !== null && (
              <p className="mt-3 text-sm" aria-live="polite">
                Removed this organization from {affected} connected app{affected === 1 ? "" : "s"}.
              </p>
            )}
            {policyMutation.isError && (
              <div className="mt-3">
                <Alert variant="danger" title="Could not change the setting">
                  {apiErrorMessage(policyMutation.error)}
                </Alert>
              </div>
            )}
          </CardBody>
        </Card>
      )}
      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        title="Turn off connectors and agents"
        message="This removes the organization from all connected apps immediately, and agents bound to it stop getting access to it. Turning it back on restores nothing: users must consent again."
        confirmText="Turn off"
        variant="danger"
        onConfirm={() => policyMutation.mutate(false)}
      />

      {treeQuery.data && (treeQuery.data.parent || (treeQuery.data.children ?? []).length > 0) && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-1 text-lg font-semibold">Organization tree</h2>
            <p className="mb-3 text-xs text-[var(--color-neutral-500)]">
              Structure only. A parent has no access to a child's members or data.
            </p>
            {treeQuery.data.parent && (
              <p className="text-sm">
                Parent: <span className="font-medium">{treeQuery.data.parent.name}</span>{" "}
                <span className="font-mono text-xs">({treeQuery.data.parent.slug})</span>
              </p>
            )}
            {(treeQuery.data.children ?? []).length > 0 && (
              <ul className="mt-2 space-y-1 text-sm">
                {(treeQuery.data.children ?? []).map((c) => (
                  <li key={c.id}>
                    Child: <span className="font-medium">{c.name}</span>{" "}
                    <span className="font-mono text-xs">({c.slug})</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
